import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Rank } from '../../types';
import { findCodePrefix, nextSequence } from '../../utils/codePrefixUtils';
import Input from '../Form/Input';
import TextArea from '../Form/TextArea';
import Select from '../Form/Select';
import Button from '../Form/Button';
import { useCodePrefixes } from '../../hooks/useCodePrefixes';

interface RankFormProps {
  rank?: Rank;
  onSubmit: (data: Partial<Rank>) => void;
  onCancel: () => void;
}

interface RankFormData {
  code: string;
  name: string;
  arabicName: string;
  department: string;
  description: string;
  isCrew: boolean;
}

const RankForm: React.FC<RankFormProps> = ({ rank, onSubmit, onCancel }) => {
  const [selectedCodePrefix, setSelectedCodePrefix] = useState<string>('');
  const [rankNumber, setRankNumber] = useState<string>('001');

  // Fetch code prefixes
  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 }
  }), []);
  const { data: codePrefixes = [] } = useCodePrefixes(queryOptions);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue
  } = useForm<RankFormData>({
    defaultValues: rank ? {
      code: rank.code,
      name: rank.name,
      arabicName: rank.arabicName,
      department: rank.department,
      description: rank.description,
      isCrew: rank.isCrew
    } : {
      code: '',
      name: '',
      arabicName: '',
      department: '',
      description: '',
      isCrew: true
    }
  });

  // Initialize code prefix when editing
  useEffect(() => {
    if (rank && rank.code && codePrefixes.length > 0) {
      const matchingPrefix = codePrefixes.find((p: any) => 
        rank.code.startsWith(p.prefix)
      );
      if (matchingPrefix) {
        setSelectedCodePrefix(matchingPrefix.id);
        setRankNumber(rank.code.replace(matchingPrefix.prefix, ''));
      }
    }
  }, [rank, codePrefixes]);

  const departmentOptions = [
    { value: 'Navigation', label: 'Navigation' },
    { value: 'Engineering', label: 'Engineering' },
    { value: 'Deck', label: 'Deck' },
    { value: 'Catering', label: 'Catering' },
    { value: 'Radio', label: 'Radio' },
    { value: 'Medical', label: 'Medical' },
    { value: 'Administration', label: 'Administration' }
  ];

  const handleFormSubmit = (data: RankFormData) => {
    onSubmit(data);
  };

  return (
    <div className="bg-white shadow-sm rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="mb-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {rank ? 'Edit Rank' : 'Add New Rank'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {rank ? 'Update the rank information below.' : 'Fill in the details to create a new rank.'}
          </p>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Rank Code with Prefix */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rank Code *
              </label>
              <select
                value={selectedCodePrefix}
                onChange={(e) => {
                  setSelectedCodePrefix(e.target.value);
                  const prefix = findCodePrefix(codePrefixes, e.target.value);
                  if (prefix) {
                    // Auto-generate next sequence number
                    const nextNum = String(nextSequence(prefix)).padStart(3, '0');
                    setRankNumber(nextNum);
                    setValue('code', `${prefix.prefix}${nextNum}`, { shouldValidate: true });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select Code Prefix</option>
                {codePrefixes.map((prefix: any) => (
                  <option key={prefix.id} value={prefix.id}>
                    {prefix.prefix} - {prefix.name}
                  </option>
                ))}
              </select>
              {selectedCodePrefix && (
                <div className="mt-2 p-2 bg-gray-50 rounded-md border border-gray-200">
                  <span className="text-sm text-gray-600">Generated Code: </span>
                  <span className="text-sm font-semibold text-primary-600">
                    {findCodePrefix(codePrefixes, selectedCodePrefix)?.prefix}{rankNumber}
                  </span>
                </div>
              )}
              <input type="hidden" {...register('code', { required: 'Rank code is required' })} />
              {errors.code?.message && (
                <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
              )}
            </div>

            <Input
              label="Rank Name *"
              {...register('name', { required: 'Rank name is required' })}
              error={errors.name?.message}
              placeholder="e.g., Captain, Chief Engineer"
            />

            <Input
              label="Arabic Name *"
              {...register('arabicName', { required: 'Arabic name is required' })}
              error={errors.arabicName?.message}
              placeholder="e.g., كابتن، كبير مهندسين"
              dir="rtl"
            />

            <Select
              label="Department *"
              {...register('department', { required: 'Department is required' })}
              error={errors.department?.message}
              options={departmentOptions}
            />
          </div>

          {/* Description */}
          <TextArea
            label="Description"
            {...register('description')}
            error={errors.description?.message}
            placeholder="Brief description of the rank responsibilities and requirements"
            rows={3}
          />

          {/* Crew Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isCrew"
              {...register('isCrew')}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="isCrew" className="ml-2 block text-sm text-gray-900">
              Is Crew Rank (appears in crew change forms and active crew list calculations)
            </label>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : rank ? 'Update Rank' : 'Create Rank'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RankForm;
